import PropTypes from "prop-types";
import React, { useState } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Form, Input, FormFeedback, Label } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser } from "../../store/actions";
import logo from "assets/images/logo-white.png";
import AuthHeader from "components/Common/AuthHeader";

const Login = props => {
  document.title = "Giriş | LojGo";

  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { identifier: "", password: "" },
    validationSchema: Yup.object({
      identifier: Yup.string()
        .required("Lütfen e-posta veya telefon numaranızı giriniz")
        .test(
          "email-or-phone",
          "Geçerli bir e-posta adresi veya telefon numarası giriniz",
          (value) => {
            if (!value) return false;
            const trimmed = value.trim();
            const isEmail = /\S+@\S+\.\S+/.test(trimmed);
            const digits = trimmed.replace(/\D/g, "");
            const isPhone = digits.length >= 10 && digits.length <= 13;
            return isEmail || isPhone;
          }
        ),
      password: Yup.string().required("Lütfen şifrenizi giriniz"),
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, props.router.navigate));
    },
  });

  const LoginProperties = createSelector(
    (state) => state.Login,
    (login) => ({ error: login.error })
  );
  const { error } = useSelector(LoginProperties);

  return (
    <React.Fragment>
      <AuthHeader />
      <div
        style={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #f5f7ff 0%, #e8eeff 50%, #f0f4ff 100%)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container>
          <Row className="justify-content-center align-items-center" style={{ minHeight: "calc(100vh - 80px)" }}>
            <Col md={9} lg={7} xl={6}>

              {/* Logo */}
              <div className="text-center mb-4">
                <Link to="/">
                  <div
                    style={{
                      width: 82, height: 82, borderRadius: 18,
                      background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 8px 24px rgba(85,110,230,0.4)",
                      marginBottom: 12,
                    }}
                  >
                    <img src={logo} alt="LojGo" height="36"  />
                  </div>
                </Link>
                <h3 style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>Tekrar Hoş Geldiniz!</h3>
                <p style={{ color: "#6c757d", fontSize: 15 }}>LojGo hesabınıza giriş yapın</p>
              </div>

              <Card
                style={{
                  border: "none",
                  borderRadius: 20,
                  boxShadow: "0 20px 60px rgba(85,110,230,0.12)",
                  overflow: "hidden",
                }}
              >
                {/* Üst gradient çizgi */}
                <div
                  style={{
                    height: 4,
                    background: "linear-gradient(90deg, #556ee6 0%, #6f42c1 50%, #e83e8c 100%)",
                  }}
                />

                <CardBody style={{ padding: "2.5rem" }}>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {error && (
                      <Alert
                        color="danger"
                        className="d-flex align-items-center gap-2"
                        style={{ borderRadius: 10, border: "none", background: "#fff5f5" }}
                      >
                        <i className="bx bx-error-circle fs-5 text-danger" />
                        <span>{error}</span>
                      </Alert>
                    )}

                    {/* E-posta veya Telefon */}
                    <div className="mb-4">
                      <Label className="form-label fw-semibold" style={{ color: "#344050", fontSize: 14 }}>
                        E-posta veya Telefon Numarası
                      </Label>
                      <div className="input-group">
                        <span
                          className="input-group-text"
                          style={{
                            background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                            border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                            padding: "0 14px",
                          }}
                        >
                          <i className="bx bx-user" style={{ fontSize: 18 }} />
                        </span>
                        <Input
                          name="identifier"
                          type="text"
                          placeholder="ornek@email.com veya 5321234567"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.identifier}
                          invalid={!!(validation.touched.identifier && validation.errors.identifier)}
                          style={{
                            borderLeft: "none", borderRadius: "0 10px 10px 0",
                            padding: "12px 14px", fontSize: 15, border: "1.5px solid #dee2e6",
                          }}
                        />
                        {validation.touched.identifier && validation.errors.identifier && (
                          <FormFeedback>{validation.errors.identifier}</FormFeedback>
                        )}
                      </div>
                    </div>

                    {/* Şifre */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label className="form-label fw-semibold mb-0" style={{ color: "#344050", fontSize: 14 }}>
                          Şifre
                        </Label>
                        <Link
                          to="/forgot-password"
                          style={{ fontSize: 13, color: "#556ee6", textDecoration: "none" }}
                        >
                          Şifremi unuttum
                        </Link>
                      </div>
                      <div className="input-group">
                        <span
                          className="input-group-text"
                          style={{
                            background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                            border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                            padding: "0 14px",
                          }}
                        >
                          <i className="bx bx-lock-alt" style={{ fontSize: 18 }} />
                        </span>
                        <Input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Şifrenizi girin"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password}
                          invalid={!!(validation.touched.password && validation.errors.password)}
                          style={{
                            borderLeft: "none", borderRight: "none", borderRadius: 0,
                            padding: "12px 14px", fontSize: 15, border: "1.5px solid #dee2e6",
                          }}
                        />
                        <span
                          className="input-group-text"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            background: "#f8f9ff", border: "1.5px solid #dee2e6", borderLeft: "none",
                            borderRadius: "0 10px 10px 0", cursor: "pointer", padding: "0 14px",
                          }}
                        >
                          <i
                            className={showPassword ? "bx bx-hide" : "bx bx-show"}
                            style={{ fontSize: 18, color: "#8592c0" }}
                          />
                        </span>
                        {validation.touched.password && validation.errors.password && (
                          <FormFeedback>{validation.errors.password}</FormFeedback>
                        )}
                      </div>
                    </div>

                    {/* Beni hatırla */}
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="form-check"
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="rememberMe"
                          style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#556ee6" }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="rememberMe"
                          style={{ cursor: "pointer", color: "#6c757d", fontSize: 14, marginBottom: 0 }}
                        >
                          Beni hatırla
                        </label>
                      </div>
                    </div>

                    {/* Giriş butonu */}
                    <button
                      type="submit"
                      style={{
                        width: "100%", padding: "13px",
                        background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                        border: "none", borderRadius: 12, color: "#fff",
                        fontWeight: 700, fontSize: 16,
                        boxShadow: "0 8px 20px rgba(85,110,230,0.45)",
                        transition: "all 0.2s",
                        letterSpacing: 0.5,
                      }}
                    >
                      <i className="bx bx-log-in me-2" style={{ fontSize: 18, verticalAlign: "middle" }} />
                      Giriş Yap
                    </button>

                    {/* Divider */}
                    <div
                      className="d-flex align-items-center my-4"
                      style={{ gap: 12 }}
                    >
                      <div style={{ flex: 1, height: 1, background: "#e9ecef" }} />
                      <span style={{ color: "#adb5bd", fontSize: 13, whiteSpace: "nowrap" }}>
                        Hesabınız yok mu?
                      </span>
                      <div style={{ flex: 1, height: 1, background: "#e9ecef" }} />
                    </div>

                    {/* Kayıt ol butonu */}
                    <Link to="/register" style={{ textDecoration: "none" }}>
                      <button
                        type="button"
                        style={{
                          width: "100%", padding: "12px",
                          background: "#fff",
                          border: "2px solid #556ee6", borderRadius: 12,
                          color: "#556ee6", fontWeight: 700, fontSize: 15,
                          transition: "all 0.2s",
                        }}
                      >
                        <i className="bx bx-user-plus me-2" style={{ fontSize: 17, verticalAlign: "middle" }} />
                        Yeni Hesap Oluştur
                      </button>
                    </Link>
                  </Form>
                </CardBody>
              </Card>

              {/* Alt bilgi */}
              <div className="text-center mt-4">
                <p style={{ color: "#adb5bd", fontSize: 12 }}>
                  &copy; {new Date().getFullYear()} LojGo. Tüm hakları saklıdır.
                </p>
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default withRouter(Login);
